// Root-cause analysis (startRca, submitRcaFeedback) helpers.
//
// The start_rca Lambda reads the release-tracker record itself and is the
// authority on eligibility; canStartRca mirrors its rule only so the dashboard
// does not offer a button that can only be refused.

import {
  DeploymentRecord,
  RcaFeedback,
  RcaFeedbackInput,
  RcaInfo,
  RcaStartResult,
  RcaVerdict,
} from '../../types/DeploymentRecord';
import { formatTimestamp } from './format';

const rejected = (s?: string): boolean => s?.toLowerCase() === 'rejected';

/**
 * True for a failed Argo deployment the start_rca Lambda will accept: not an
 * auto-rollback, not a plan a reviewer rejected, and not in a dynamic
 * environment (only those records carry a ttl).
 */
export const canStartRca = (record: DeploymentRecord): boolean =>
  !!record.deployment_id &&
  record.status?.toLowerCase() === 'failed' &&
  record.metadata?.engine === 'argo' &&
  record.deploy_kind !== 'rollback' &&
  !rejected(record.decision) &&
  !rejected(record.approval_decision) &&
  record.ttl == null;

/**
 * True while the record says an analysis is queued or running. The Lambda
 * refuses a second run on its own; this only disables the button early.
 */
export const rcaInFlight = (record: DeploymentRecord): boolean => {
  const status = record.rca?.status;
  return status === 'pending' || status === 'running';
};

/** The feedback line shown after a startRca request resolves. */
export const rcaStartMessage = (result: RcaStartResult): string =>
  result.status === 'already_running'
    ? `An analysis is already running (${result.workflow})`
    : `Analysis started (${result.workflow})`;

/** Confidence (0..1) as a whole percentage, or null when absent. */
export const formatConfidence = (confidence?: number): string | null =>
  typeof confidence === 'number' && Number.isFinite(confidence)
    ? `${Math.round(Math.min(1, Math.max(0, confidence)) * 100)}%`
    : null;

// --- Feedback (submitRcaFeedback) --------------------------------------------
// The start_rca Lambda validates the same rules; these only keep the
// form from sending what it would refuse.

/** The rca.category enum, in cd-pipeline-infra's schema order. */
export const RCA_CATEGORIES = ['code', 'config', 'infra', 'dependency', 'pipeline', 'flaky', 'unknown'];

/** The Lambda's cap on actual_cause, in characters (code points). */
export const MAX_RCA_CAUSE_LENGTH = 1000;

/** True for an analysis that can be rated: finished, and naming its run. */
export const canRateRca = (rca?: RcaInfo): boolean => rca?.status === 'done' && !!rca.workflow_name;

/** The verdict on the run rca shows, ignoring one left over from an earlier run. */
export const currentRcaFeedback = (rca?: RcaInfo): RcaFeedback | undefined =>
  rca?.feedback && rca.workflow_name && rca.feedback.workflow_name === rca.workflow_name
    ? rca.feedback
    : undefined;

/** The feedback form's state. categoryCorrect is undefined until answered. */
export interface RcaFeedbackDraft {
  verdict: RcaVerdict | null;
  categoryCorrect?: boolean;
  actualCategory: string;
  actualCause: string;
}

/** Why draft cannot be submitted yet, or null when it can. */
export const rcaFeedbackProblem = (draft: RcaFeedbackDraft): string | null => {
  if (!draft.verdict) return 'Choose helpful or not helpful';
  if (draft.categoryCorrect === false && !draft.actualCategory) return 'Pick the category it actually was';
  if (Array.from(draft.actualCause.trim()).length > MAX_RCA_CAUSE_LENGTH) {
    return `Keep the cause to ${MAX_RCA_CAUSE_LENGTH} characters`;
  }
  return null;
};

/**
 * The submitRcaFeedback frame. actual_category goes only with a "No" to
 * category_correct, which is the only time the Lambda accepts it from here.
 */
export const rcaFeedbackMessage = (input: RcaFeedbackInput): Record<string, unknown> => {
  const cause = input.actualCause?.trim();
  return {
    action: 'submitRcaFeedback',
    deployment_id: input.deploymentId,
    workflow_name: input.workflowName,
    verdict: input.verdict,
    ...(input.categoryCorrect !== undefined ? { category_correct: input.categoryCorrect } : {}),
    ...(input.categoryCorrect === false && input.actualCategory ? { actual_category: input.actualCategory } : {}),
    ...(cause ? { actual_cause: cause } : {}),
  };
};

/** "Rated helpful by <email> · <time>" for a saved verdict. */
export const rcaFeedbackSummary = (feedback: RcaFeedback): string =>
  `Rated ${feedback.verdict === 'up' ? 'helpful' : 'not helpful'} by ${feedback.by}` +
  (feedback.at ? ` · ${formatTimestamp(feedback.at)}` : '');

/** The form state to edit a saved verdict from. */
export const draftFromRcaFeedback = (feedback: RcaFeedback): RcaFeedbackDraft => ({
  verdict: feedback.verdict,
  categoryCorrect: feedback.category_correct,
  actualCategory: feedback.actual_category ?? '',
  actualCause: feedback.actual_cause ?? '',
});
